import React, { forwardRef, useEffect, useState } from 'react';
import styles from './OrganisationAbnAcn.module.scss';
import abnAcnService from './OrganisationAbnAcnService';
import Select from 'react-select';
import { validate, isCompleted } from '../Utils/Validation';
import FormError from '../FormError/FormError';
import { Notification, ComponentLoader, Button, FormInput } from '@snsw/react-component-library';

const OrganisationAbnAcn = (props, ref) => {
  const SAME_AS_ABOVE = 'Same as Above';
  const defaultTradingName = { value: SAME_AS_ABOVE, label: SAME_AS_ABOVE };
  const [results, setResults] = useState([]);
  const [loaded, setLoaded] = useState(results && results.length);
  const [loading, setLoading] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [currentAbn, setCurrentAbn] = useState(props.value || '');

  const clearResult = () => {
    setResults([]);
    setLoaded(false);
    props.setAbn('');
    props.setAcn('');
    props.setBusinessName('');
    props.setSelectedTradingName('');
  };

  useEffect(() => {
    setShowResult(false);
    clearResult();
  }, [currentAbn]);

  useEffect(() => {
    if (currentAbn) {
      handleLookup(currentAbn);
    }
  }, []);

  const retrieveValue = async (searchValue) => {
    setResults([]);
    setLoaded(false);
    if (searchValue.length < 9 || searchValue.length > 11) {
      return;
    }
    setLoading(true);
    const searchResponse = await abnAcnService.search(`${props.searchUrl}`, searchValue);
    setShowResult(true);

    if (searchResponse.status === 200) {
      errors.currentAbn = false;
      setResults(searchResponse.data);
      setLoaded(true);
      if (props.setSelectedTradingName) {
        // To reset the default selection in case a new search is performed.
        if (props.selectedTradingName.length === 0) {
          props.setSelectedTradingName(defaultTradingName);
        } else {
          props.setSelectedTradingName(props.selectedTradingName);
        }
      }
    } else {
      setResults([]);
      setLoaded(false);
    }
    setLoading(false);
  };

  const handleLookup = async (value) => {
    setCurrentAbn(value);
    if (props.setValue) {
      props.setValue(value);
    }
    await retrieveValue(value);
  };

  const [errors, setErrors] = useState({
    currentAbn: false
  });

  ///Rules for trading name: 1. show 'Same as above' 1st. 2. combine the tradingName and businessName together from ABR.
  const transformNames = (businessNames, tradingNames) => {
    return [defaultTradingName]
      .concat((businessNames || []).map((label) => ({ value: label, label })))
      .concat((tradingNames || []).map((label) => ({ value: label, label })));
  };

  const [showErrors, setShowErrors] = useState(false);
  const validators = [
    {
      fieldName: 'currentAbn',
      fieldValue: currentAbn,
      isValid: (v) => isCompleted(v) && results.length
    }
  ];

  useEffect(() => {
    if (currentAbn.length >= 1) {
      setShowErrors(false);
    }
  }, [currentAbn]);

  if (ref) {
    ref.current = {
      validate: () => {
        const isValid = validate(validators, setErrors);
        setShowErrors(!isValid);
        errors.currentAbn = true;
        return isValid;
      }
    };
  }

  const normaliseAbnAcn = (abnAcn) => {
    const longOne = `       ${abnAcn}`;
    const shortenedOne = longOne.slice(longOne.length - 12);
    return shortenedOne.replace(/((\d|\ ){3})/g, '$1 ').replace(/(^\s+|\s+$)/, '');
  };

  return (
    <>
      <div className={`row ${styles.searchBox}`}>
        <div className={styles.searchInput}>
          <FormInput
            id={'abnAcnSearchInput'}
            errorMessage={'Registration Number is required.'}
            hasError={showErrors && errors.currentAbn}
            placeholder={props.type}
            label={props.title}
            value={currentAbn}
            onChange={async (e) => {
              let numericVals = e.target.value.replace(/\D/g, '');
              if (numericVals.length > 11) {
                numericVals = numericVals.substring(0, maxChar);
              }
              setCurrentAbn(numericVals);
            }}
          />
        </div>
        <div className={styles.searchButton}>
          <Button
            theme="secondary"
            onClick={async (e) => {
              await handleLookup(currentAbn);
            }}
            onKeyDown={async (e) => {
              if (e.keyCode === 13) {
                await handleLookup(currentAbn);
              }
            }}
          >
            Search
          </Button>
        </div>
      </div>

      {loading && (
        <ComponentLoader className={`${styles.loading} ${props.title ? styles.loadingWithTitle : ''}`} />
        /* <Loading
          className={`${styles.loading} ${props.title ? styles.loadingWithTitle : ''}`}
          label={false}
          type={'ajax'}
       />*/
      )}
      {showResult ? (
        <>
          {!!loaded &&
            (results.length > 0 && results.filter((obj) => obj.status !== 'Cancelled').length > 0 ? (
              results
                .filter((obj) => obj.status !== 'Cancelled')
                .map((a) => (
                  <div key={a.abn}>
                    <div className={'row'}>
                      <div className={`col-12`}>
                        <div key={JSON.stringify(a)} className={styles.detail} id={'abnAcnSearchResult'}>
                          <div className={styles.titleLine}>
                            <h3 className={styles.titleLeft}>{a.name}</h3>
                            <div className={styles.titleRight}>
                              <a
                                href={'#'}
                                onClick={(e) => {
                                  e.preventDefault();
                                  clearResult();
                                }}
                              >
                                Clear
                              </a>
                            </div>
                          </div>
                          <div className={styles.detailLine}>
                            <div className={styles.detailLeft}>ABN</div>
                            <div className={styles.detailRight}>{a.abn}</div>
                          </div>
                          {props.setAbn(normaliseAbnAcn(a.abn))}
                          {props.setAcn(normaliseAbnAcn(a.acn))}
                          {props.setBusinessName(a.name)}
                          {props.setAllBusinessNames ? props.setAllBusinessNames(a.businessNames) : null}
                        </div>
                      </div>
                    </div>
                    {props.hideTradingNameList && props.hideTradingNameList === true ? null : (
                      <div className={'row'}>
                        <div className={`col-12 ${styles.section}`}>
                          <p className={styles.title}>Registered Trading Name</p>
                          <Select
                            options={transformNames(a.businessNames, a.tradingNames)}
                            value={props.selectedTradingName}
                            onChange={(evt) => {
                              props.setSelectedTradingName(evt);
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ))
            ) : (
              <div className={'row'}>
                <div className={`col-12`}>
                  <FormError value={`The number entered is not a valid ${props.type}.`} />
                </div>
              </div>
            ))}
        </>
      ) : null}
    </>
  );
};

export default forwardRef(OrganisationAbnAcn);
